import { ReactNode } from 'react';
import { ChartExportSection } from 'components/chart-export';
import { MineralType } from 'types/minerals';
import { VolumeChart2 } from './decline-curve-analysis/volume-chart-2';
import type { ForecastData } from './types';
import {
  ChartActions,
  type VolumeDisplayMode,
} from './decline-curve-analysis/chart-actions';
import style from './well-analysis-chart-panel.module.scss';

const NO_MARKERS = { segmentation: false, outlier: false };
const VOLUME_STREAMS = ['oil', 'gas', 'water'];
const NOOP = () => {};

export interface WellAnalysisChartPanelProps {
  /** Production volumes for the version the chart draws as its solid line. */
  production: {
    volume: ForecastData['volume'];
    cumulative: ForecastData['cumulative'];
    ratio: ForecastData['ratio'];
    materialBalanceTime: ForecastData['materialBalanceTime'];
  };
  /** Whether the production volumes are already daily, so the Daily display leaves them as they are. */
  productionVolumeIsDailyRate?: boolean;
  /**
   * Everything drawn on top of the production line: the selected forecasts,
   * dashed, plus any production version beyond the first, solid.
   */
  forecasts: ForecastData[];
  isLoading: boolean;
  /** Fixed canvas height. Without one the chart keeps a 2:1 shape. */
  height?: number;
  selectedStreams: MineralType[];
  onStreamToggle: (stream: MineralType) => void;
  volumeDisplayMode: VolumeDisplayMode;
  onVolumeDisplayModeChange: (mode: VolumeDisplayMode) => void;
  /** Names of the selected production versions, listed under the chart. */
  productionVersionLabels: string[];
  /** Names of the selected forecast versions, listed under the chart. */
  forecastVersionLabels: string[];
  /** Icon buttons for the title band: expand and edit. */
  headerActions?: ReactNode;
  /** EUR figures for the title band, in the DCA charts' format. */
  headerStats?: Array<{
    label: string;
    value: number | null | undefined;
    unit: string;
  }>;
  /**
   * Prefix for the controls' ids. The section and the expanded chart are both
   * in the document at once, so each needs its own.
   */
  controlsId: string;
}

/**
 * The Well Analysis chart and the controls that read against it: the volumes
 * chart itself, the stream legend and volume-display switch below it, and the
 * versions each line comes from.
 *
 * The section and the expanded-chart tearsheet both render this, so the two
 * show the same chart with the same controls.
 *
 * @param {WellAnalysisChartPanelProps} props - Series to draw and the controls' state.
 * @return {JSX.Element} The chart panel.
 */
export const WellAnalysisChartPanel = ({
  production,
  productionVolumeIsDailyRate = false,
  forecasts,
  isLoading,
  height,
  selectedStreams,
  onStreamToggle,
  volumeDisplayMode,
  onVolumeDisplayModeChange,
  productionVersionLabels,
  forecastVersionLabels,
  headerActions,
  headerStats,
  controlsId,
}: WellAnalysisChartPanelProps) => {
  const title =
    volumeDisplayMode === 'daily' ? 'Daily Volumes' : 'Monthly Volumes';

  return (
    <>
      {/* Gives the chart's download button its own export drawer, beside the
          chart. The page's shared drawer is in another section. */}
      <ChartExportSection>
        <VolumeChart2
          type="monthly"
          title={title}
          height={height}
          production={production}
          productionVolumeIsDailyRate={productionVolumeIsDailyRate}
          typeCurveVolumes={null}
          forecasts={forecasts}
          selectedSeries={selectedStreams}
          isLoading={isLoading}
          showMarkers={NO_MARKERS}
          volumeDisplayMode={volumeDisplayMode}
          onDragEnd={NOOP}
          onDraggableSeriesUpdate={NOOP}
          initialAutoZoom
          header={headerActions}
          headerStats={headerStats}
          exportConfig={{
            label: title,
            filenamePrefix: 'well-analysis-volumes',
          }}
        />
      </ChartExportSection>

      {/* data-keep-in-view marks what the expanded chart keeps on screen under it. */}
      <div data-keep-in-view>
        <ChartActions
          selectedLegend={selectedStreams}
          handleLegendClick={onStreamToggle}
          showMarkers={NO_MARKERS}
          setShowMarkers={NOOP}
          enableMarkers={false}
          showTypeCurve={false}
          setShowTypeCurve={NOOP}
          enableTypeCurve={false}
          volumeDisplayMode={volumeDisplayMode}
          onVolumeDisplayModeChange={onVolumeDisplayModeChange}
          streams={VOLUME_STREAMS}
          testId={controlsId}
        />
      </div>

      <div data-keep-in-view className={style.versionLegend}>
        <VersionLegendGroup
          heading="Production"
          labels={productionVersionLabels}
        />
        <VersionLegendGroup
          heading="PDP Forecast (- - -)"
          labels={forecastVersionLabels}
        />
      </div>
    </>
  );
};

const VersionLegendGroup = ({
  heading,
  labels,
}: {
  heading: string;
  labels: string[];
}) => (
  <div className={style.versionLegendGroup}>
    <h4 className={style.versionLegendHeading}>{heading}</h4>
    {labels.length > 0 ? (
      labels.map((label) => (
        <p key={label} className={style.versionLegendLabel}>
          {label}
        </p>
      ))
    ) : (
      <p className={style.versionLegendLabel}>None selected</p>
    )}
  </div>
);
